<template>
  <div
    ref="addressLookupContainer"
    class="mb-3"
  >
    <label
      for="addressSearchTerm"
      class="form-label"
    >{{ label }}</label>

    <div
      class="dropdown"
    >
      <div class="input-group">
        <input
          id="addressSearchTerm"
          v-model="addressSearchTerm"
          type="search"
          autocomplete="off"
          :class="['form-control', { 'is-invalid': errors?.length }]"
          :placeholder="$localize('AddressSearchTermPlaceholder')"
          @blur="onBlur"
          @keydown="searchOnEnterKey"
        >
        <button
          :disabled="!addressSearchTerm"
          class="btn btn-outline-secondary input-group-btn"
          type="button"
          @click.stop.prevent="search"
          @blur="onBlur"
        >
          <span
            class="fa-regular fa-search"
            aria-hidden="true"
          />
          <span
            class="d-none d-lg-inline ms-1"
            aria-hidden="true"
          >{{ $localize('Search') }}</span>
          <span
            class="visually-hidden"
          >{{ $localize('Search') }}</span>
        </button>
      </div>
      <button
        ref="dropdownToggleElement"
        class="visually-hidden"
        aria-hidden="true"
        tabindex="-1"
        data-bs-toggle="dropdown"></button>
      <ul
        class="dropdown-menu w-100"
      >
        <li
          v-if="!addressSearchResults.length && searching"
        >
          <a class="dropdown-item disabled">{{ $localize('AddressSearchInProgress') }}</a>
        </li>

        <li
          v-else-if="!addressSearchResults.length && !searching"
        >
          <a class="dropdown-item disabled">{{ $localize('AddressSearchNoResults') }}</a>
        </li>

        <li
          v-for="(address, index) in addressSearchResults"
          v-else
          :key="index"
        >
          <a
            class="dropdown-item"
            href="#"
            :title="address.name"
            @click.prevent="setLocation(address)"
            @blur="onBlur"
          >{{ address.name }}</a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue';

  import addressLookupService from '@/Services/addressLookupService';
  import { ApplicationError } from '@/Services/Clients/applicationError';
  import { KeyboardCodes } from '@/Types/Enums/keyBoardCodes';
  import logger from '@/Utils/logger';
  import MapCoordinate from '@/VueComponents/SharedComponents/Map/Models/mapCoordinate';
  import useBootstrapApi from '@/VueCore/utils/useBootstrapApi';

  defineProps<{
    label: string,
    errors?: string[]
  }>();

  const addressSearchTerm = ref('');
  const addressSearchResults = ref([]);
  const searching = ref(false);

  const addressLookupContainer = ref<HTMLElement>();
  const dropdownToggleElement = ref<HTMLElement>();

  const emit = defineEmits(['change']);

  const coordinate = defineModel<MapCoordinate>();
  const bsDropdown = useBootstrapApi(bootstrap => new bootstrap.Dropdown(dropdownToggleElement.value!));

  function search() {
    if (!addressSearchTerm.value.length) {
      return;
    }

    logger.debug('looking up address for \'%s\'', addressSearchTerm.value);

    addressSearchResults.value = [];
    searching.value = true;
    bsDropdown.value?.show();

    addressLookupService.lookupAddress(addressSearchTerm.value)
      .then(function (results) {
        addressSearchResults.value = results;

        searching.value = false;

        logger.debug('received %d results', results.length);
      })
      .catch((error: ApplicationError) => {

        bsDropdown.value?.hide();

        if (!error.isLogged) {
          logger.error('UnexpectedErrorWhileRetrievingAddressLookup', null, error);
        }
      });
  }

  function setLocation(address) {
    logger.debug('setting location to %f, %f', address.coordinate.latitude, address.coordinate.longitude);

    coordinate.value = { latitude: address.coordinate.latitude, longitude: address.coordinate.longitude };

    bsDropdown.value?.hide();
    emit('change', { latitude: address.coordinate.latitude, longitude: address.coordinate.longitude });
  }

  function searchOnEnterKey(event: KeyboardEvent) {
    if (event.key === KeyboardCodes.Enter) {
      search();
    }
  }

  function onBlur(event: MouseEvent) {
    if (isEventTargetWithinContainer(event.relatedTarget as HTMLElement)) {
      return;
    }

    bsDropdown.value?.hide();
    addressSearchResults.value = [];
  }

  function isEventTargetWithinContainer(target: HTMLElement | undefined | null) {
    if (!target) {
      return false;
    }

    if (target === document.body) {
      return false;
    }

    if (target === addressLookupContainer.value) {
      return true;
    }

    return isEventTargetWithinContainer(target.parentElement);
  }
</script>
<style lang="scss" scoped>
.dropdown-menu {
  overflow-x: hidden;
}
</style>
